import * as React from 'react';
import { flatMap } from 'lodash';
import useForm from 'antd/lib/form/hooks/useForm';
import Text from 'antd/lib/typography/Text';
import Modal from 'antd/lib/modal/Modal';
import Button from 'antd/lib/button';
import Form from 'antd/lib/form/Form';
import FormItem from 'antd/lib/form/FormItem';
import Input from 'antd/lib/input';
import { NewPasswordRequest, tryNewPassword } from '@/actions/user';
import { selectUser } from '@/selectors/selectors';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '@/reducers/reducers';
import { globalMessage } from '@/utils/globalMessage';
import { tryLogout } from '@/actions/login';
import { UserUpdatable } from '@/actions/updatable';
import { useError, useIsLoading, useUpdated } from '@/utils/hooks/helpers';

const NewPasswordComponent: React.FC = () => {
    const [formIsValid, setFormIsValid] = React.useState(false);
    const [form] = useForm();
    const dispatch = useDispatch();

    const isLoading = useIsLoading(UserUpdatable.TRY_NEW_PASSWORD);
    const isUpdated = useUpdated(UserUpdatable.TRY_NEW_PASSWORD);
    const isError = useError(UserUpdatable.TRY_NEW_PASSWORD);

    const userId = useSelector((state: RootState) => selectUser(state).id);
    const userEmail = useSelector((state: RootState) => selectUser(state).email);
    const needsNewPassword = useSelector((state: RootState) => selectUser(state).needsNewPassword);

    const onFieldsChange = () => {
        const errors = flatMap(form.getFieldsError().map((field) => field.errors));
        const values = form.getFieldsValue();
        const allFieldsHaveValues = values['newPassword'] && values['newPasswordConfirm'];

        setFormIsValid(errors.length === 0 && allFieldsHaveValues);
    };

    const onFinish = () => {
        form.validateFields().then((values) => {
            createNewPassword(values);
        });
    };

    const createNewPassword = (values) => {
        const request: NewPasswordRequest = {
            userId,
            newPassword: values.newPassword,
        };

        dispatch(tryNewPassword(request));
    };

    const formLayout = {
        wrapperCol: { span: 14 },
        labelCol: { span: 10 },
    };

    React.useEffect(() => {
        if (isError) {
            globalMessage.error(isError);
        }
    }, [isError]);

    React.useEffect(() => {
        if (isUpdated) {
            globalMessage.success('Password successfully updated! Please sign in again.');

            dispatch(tryLogout());
        }
    }, [isUpdated]);

    return (
        <Modal
            title="Change password"
            open={needsNewPassword}
            closable={false}
            footer={
                <Button type="primary" onClick={() => form.submit()} disabled={!formIsValid} loading={isLoading}>
                    Save new password
                </Button>
            }
        >
            <div>
                <Text>
                    You&apos;re logged in as <Text strong>{userEmail}</Text>. Please create a new password.
                </Text>
                <Form
                    onFieldsChange={onFieldsChange}
                    onFinish={onFinish}
                    {...formLayout}
                    labelAlign="left"
                    style={{ marginTop: 24 }}
                    form={form}
                >
                    <FormItem
                        label="New password"
                        name="newPassword"
                        rules={[{ required: true, message: 'This field is required.' }]}
                    >
                        <Input.Password />
                    </FormItem>
                    <FormItem
                        label="Confirm new password"
                        name="newPasswordConfirm"
                        rules={[
                            { required: true, message: 'This field is required.' },
                            { min: 12, message: 'Password must be at least 12 characters long.' },
                            {
                                validator: (rule, value) => {
                                    const hasUpperCase = value.match(/[A-Z]/);
                                    if (hasUpperCase) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject();
                                    }
                                },
                                message: 'Password must contain at least one uppercase letter.',
                            },
                            {
                                validator: (rule, value) => {
                                    const hasANumber = value.match(/[0-9]/);
                                    if (hasANumber) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject();
                                    }
                                },
                                message: 'Password must contain at least one number.',
                            },
                            {
                                validator: (rule, value) => {
                                    const hasLowerCase = value.match(/[a-z]/);
                                    if (hasLowerCase) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject();
                                    }
                                },
                                message: 'Password must contain at least one lowercase letter.',
                            },
                            {
                                validator: (rule, value) => {
                                    const hasSymbol = value.match(/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/);
                                    if (hasSymbol) {
                                        return Promise.resolve();
                                    } else {
                                        return Promise.reject();
                                    }
                                },
                                message: 'Password must contain at least one symbol.',
                            },
                            ({ getFieldValue }) => ({
                                validator(rule, value) {
                                    if (!value || getFieldValue('newPassword') === value) {
                                        return Promise.resolve();
                                    }
                                    return Promise.reject('The two passwords must match.');
                                },
                            }),
                        ]}
                    >
                        <Input.Password />
                    </FormItem>
                </Form>
                <Text>New password must contain:</Text>
                <ul>
                    <li>At least 12 characters</li>
                    <li>At least 1 number</li>
                    <li>At least 1 lowercase letter</li>
                    <li>At least 1 uppercase letter</li>
                    <li>At least 1 of the following symbols: !@#~$%^&*()+|_</li>
                </ul>
            </div>
        </Modal>
    );
};

export default NewPasswordComponent;
